import { InfoIcon } from '@/assets/icons/info-icons/info';
import { Banner } from '@/components/basicComponents/banner';
import RadioButton from '@/components/basicComponents/radioButton';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Label from '@/components/basicComponents/label';
import FooterModal from '@/components/settings-page/importData/footer';
import { ProgressBar } from '@/components/settings-page/importData/progressBar';
import { ImportWrapper } from '@/components/settings-page/importData/styles';
import { useResponsive } from '@/hooks/use-responsive';
import { themeVar } from '@/styles/theme';
import { AnimatePresence, motion } from 'framer-motion';
import { Control, Controller, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { ICreateAutoPlanForm } from '..';

interface AutoPlanProps {
  autoPlanWizardSteps: { name: string; status: string }[];
  control: Control<ICreateAutoPlanForm, any>;
  isSubmitting: boolean;
  prevStep: () => void;
  nextStep: () => void;
}

const automaticallyGenerateOptions = ['Yes, create my plan and I’ll make any necessary edits', 'No, I will enter all commitment amounts manually'];

export const AutoPlan = ({ autoPlanWizardSteps, control, isSubmitting, prevStep, nextStep }: AutoPlanProps) => {
  const { isMobile } = useResponsive();
  const generateAllocationsValue = useWatch({
    control,
    name: 'generateAllocations'
  });

  return (
    <>
      <StepWrapper isMobile={isMobile}>
        <ProgressBar importSteps={autoPlanWizardSteps} />
        <StepDetailsWrapper>
          <StepTitle>Auto Plan</StepTitle>
          <StepDescription>
            For investors that are new to private capital, you may prioritize an aggressive commitment plan that ramps up to the target allocation
            quickly. Alternatively, a more relaxed pace will prioritize vintage diversification. With Auto Plan you can choose an appropriate balance.
          </StepDescription>
        </StepDetailsWrapper>
        <StepBodyWrapper isMobile={isMobile}>
          <FieldWrapper isMobile={isMobile}>
            <div>
              <Label size="md">Automatically Generate Commitment Amounts</Label>
              <Controller
                name="generateAllocations"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <>
                    {automaticallyGenerateOptions.map((option, index) => (
                      <RadioButton
                        key={option + index}
                        name="useExistingLegalEntity"
                        radioButtonText={option}
                        selectedOption={value ? automaticallyGenerateOptions[0] : automaticallyGenerateOptions[1]}
                        handleRadioChange={(event) => onChange(event.target.value === automaticallyGenerateOptions[0])}
                      />
                    ))}
                  </>
                )}
              />
            </div>
            <div>
              <AnimatePresence mode="wait">
                {generateAllocationsValue ? (
                  <motion.div
                    key="yearsToAchieveAllocationTargetSelect"
                    initial={{
                      opacity: 0
                    }}
                    animate={{
                      opacity: 1,
                      transition: {
                        duration: 0.2
                      }
                    }}
                    exit={{
                      opacity: 0,
                      transition: {
                        duration: 0.2
                      }
                    }}
                  >
                    <Controller
                      name="yearsToAchieveAllocationTarget"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Select
                          value={value}
                          setValue={onChange}
                          options={['5', '6', '7', '8', '9', '10'].map((item) => createSimpleSelectOption(item))}
                          label="Years to achieve allocation target"
                          size="lg"
                          isRequiredField
                        />
                      )}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="yearsToAchieveAllocationTargetBanner"
                    initial={{
                      opacity: 0
                    }}
                    animate={{
                      opacity: 1,
                      transition: {
                        duration: 0.2
                      }
                    }}
                    exit={{
                      opacity: 0,
                      transition: {
                        duration: 0.2
                      }
                    }}
                  >
                    <Banner
                      title=""
                      description="You will start with a completely blank plan.  You will need to provide all future commitment values."
                      icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
                      bgColor={themeVar('layer.1')}
                      color={themeVar('font.base')}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </FieldWrapper>
        </StepBodyWrapper>
      </StepWrapper>
      <FooterModal
        startOver={prevStep}
        startOverButtonName="Back"
        confirmButton={{ name: 'Save and Complete', onClick: nextStep, disabled: isSubmitting }}
      />
    </>
  );
};

const StepWrapper = styled(ImportWrapper)<{ isMobile: boolean }>`
  margin-bottom: 150px;
  padding: ${({ isMobile }) => (isMobile ? '20px 30px' : '40px 60px')};
`;

const StepDetailsWrapper = styled.div``;

const StepTitle = styled.p`
  font-size: 20px;
  font-weight: 700;
  line-height: 22.4px;
  text-align: center;
  margin-top: 10px;
  color: ${themeVar('font.base')};
`;

const StepDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  text-align: center;
  padding: 10px;
  color: ${themeVar('font.weak')};
`;

const StepBodyWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: ${({ isMobile }) => (isMobile ? '10px' : '40px')};
`;

const FieldWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 1fr')};
  gap: 10px;
`;
